import { auth } from '@/lib/firebaseConfig';
import { RegisterRequest } from '@/features/auth/types/http/requests/registerRequest';
import { ProviderType } from '@/features/auth/types/http/responses/loginResponse';
import {
  AuthProvider,
  browserSessionPersistence,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithCredential,
  signInWithPopup,
  signOut,
  unlink,
  UserCredential,
  updatePassword,
  updateProfile,
  User,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { UpdateRequest } from '@/features/auth/types/http/requests/updateRequest';
import { handleFirebaseError } from '@/utils/handleErrors';
import { FirebaseError } from 'firebase/app';
import { sendGAEvent } from '@next/third-parties/google';

const getIsNewUser = (result: UserCredential): boolean =>
  (result as any)._tokenResponse?.isNewUser ?? false;

async function authenticateWithSocialProvider({
  provider,
  credentialFromError,
}: {
  provider: AuthProvider;
  credentialFromError: (
    error: FirebaseError
  ) => ReturnType<typeof GoogleAuthProvider.credentialFromError>;
}): Promise<UserCredential> {
  if (!auth.currentUser?.isAnonymous) {
    return signInWithPopup(auth, provider);
  }

  try {
    return await linkWithPopup(auth.currentUser, provider);
  } catch (error) {
    if (
      error instanceof FirebaseError &&
      error.code === 'auth/credential-already-in-use'
    ) {
      const credential = credentialFromError(error);

      if (!credential) {
        throw error;
      }

      // Existing provider account wins; caller migrates guest data by oldUid.
      return signInWithCredential(auth, credential);
    }

    throw error;
  }
}

// register the user in the Firebase Auth (client)
export const registerUser = async (
  registerRequest: RegisterRequest
): Promise<User> => {
  try {
    await setPersistence(auth, browserSessionPersistence); // Oturumu kalıcı yap

    let user: User;
    if (auth.currentUser?.isAnonymous) {
      const credential = EmailAuthProvider.credential(
        registerRequest.email,
        registerRequest.password
      );
      const userCredential = await linkWithCredential(
        auth.currentUser,
        credential
      );
      user = userCredential.user;
    } else {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        registerRequest.email,
        registerRequest.password
      );
      user = userCredential.user;
    }

    // Update the user's display name
    await updateProfile(user, {
      displayName: registerRequest.displayName ?? 'User',
    });

    sendGAEvent('event', 'register', {
      method: 'email',
      email: user.email,
    });

    return user;
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

// login the user in the Firebase Auth (client)
export const loginUser = async ({
  email: userEmail,
  password: userPassword,
}: {
  email: string;
  password: string;
}): Promise<User> => {
  try {
    await setPersistence(auth, browserSessionPersistence);
    const userCredential = await signInWithEmailAndPassword(
      auth,
      userEmail,
      userPassword
    );

    sendGAEvent('event', 'login', {
      method: 'email',
      email: userCredential.user.email,
    });

    return userCredential.user;
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

export const loginUserWithCustomToken = async (
  customToken: string
): Promise<User> => {
  try {
    await setPersistence(auth, browserSessionPersistence);
    const userCredential = await signInWithCustomToken(auth, customToken);

    sendGAEvent('event', 'login', {
      method: 'verification_code',
      email: userCredential.user.email,
    });

    return userCredential.user;
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

// google login the user in the Firebase Auth (client)
export const loginWithGoogle = async (): Promise<{
  user: User | null;
  isNewUser: boolean;
}> => {
  try {
    await setPersistence(auth, browserSessionPersistence); // Oturumu kalıcı yap
    const provider = new GoogleAuthProvider();
    const result = await authenticateWithSocialProvider({
      provider,
      credentialFromError: GoogleAuthProvider.credentialFromError,
    });

    sendGAEvent('event', 'login', {
      method: 'google',
      email: result.user.email,
    });
    return {
      user: result.user,
      isNewUser: getIsNewUser(result),
    };
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

// facebook login the user in the Firebase Auth (client)
export const loginWithFacebook = async (): Promise<{
  user: User | null;
  isNewUser: boolean;
}> => {
  try {
    await setPersistence(auth, browserSessionPersistence); // Oturumu kalıcı yap
    const provider = new FacebookAuthProvider();
    const result = await authenticateWithSocialProvider({
      provider,
      credentialFromError: FacebookAuthProvider.credentialFromError,
    });

    sendGAEvent('event', 'login', {
      method: 'facebook',
      email: result.user.email,
    });
    return {
      user: result.user,
      isNewUser: getIsNewUser(result),
    };
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

export const forgotPassword = async ({ email }: { email: string }) => {
  try {
    await sendPasswordResetEmail(auth, email);
    sendGAEvent('event', 'forgot_password', {
      email: email,
    });
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    } else {
      throw error;
    }
  }
};

export const resetPassword = async (oobCode: string, newPassword: string) => {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
    sendGAEvent('event', 'reset_password_success', {
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

export const verifyEmail = async (user: User) => {
  try {
    await sendEmailVerification(user);
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    sendGAEvent('event', 'logout', {
      email: auth.currentUser?.email,
    });
    await signOut(auth);
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

export const updateUserProfile = async (
  request: UpdateRequest
): Promise<void> => {
  if (!auth.currentUser) {
    throw new Error('User is not authenticated');
  }

  try {
    // Handle name update
    if (request.name && request.name !== auth.currentUser.displayName) {
      await updateProfile(auth.currentUser, { displayName: request.name });
      sendGAEvent('event', 'profile_update', {
        type: 'name',
        email: auth.currentUser.email,
      });
    }
  } catch (error) {
    if (error instanceof FirebaseError) {
      handleFirebaseError(error);
    }
    throw error;
  }
};

async function unlinkSocialProviders(user: User) {
  const providers = user.providerData
    .filter(
      (provider) =>
        provider.providerId === 'google.com' ||
        provider.providerId === 'facebook.com'
    )
    .map((provider) => provider.providerId);

  for (const providerId of providers) {
    try {
      await unlink(user, providerId);
    } catch (error) {
      console.error(`Failed to unlink ${providerId}:`, error);
    }
  }
}

/**
 * Authenticates a user using the specified provider type.
 *
 * @param providerType - The type of provider to authenticate with (e.g., 'GOOGLE', 'FACEBOOK').
 * @returns A promise that resolves to a User object if authentication is successful, or null if it fails.
 * @throws An error if the provider type is invalid or if an error occurs during authentication.
 */

export async function authUsingProvider(
  providerType: ProviderType
): Promise<{ user: User | null; isNewUser: boolean } | null> {
  switch (providerType) {
    case 'GOOGLE':
      return loginWithGoogle();
    case 'FACEBOOK':
      return loginWithFacebook();
    default:
      throw new Error('Invalid provider type');
  }
}
